import type { Palette } from '@/store/palettes'

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
    ]
}

export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r},${g},${b},${alpha})`
}

export interface PacerUiColors {
  shellBg: string
  paneBg: string
  paneBorder: string
  overlay: string
  chipBg: string
  phaseIdle: string
  phaseRunning: string
  circleIdleBg: string
  countColor: string
  countSubColor: string
  beginColor: string
  beginBorder: string
  beginRunningColor: string
  beginRunningBorder: string
  doneColor: string
  doneHover: string
  panelBg: string
  labelMuted: string
  sliderEnds: string
  timing: string
  dotInactive: string
  addPhoto: string
  addPhotoBorder: string
  addPhotoBorderActive: string
  brand: string
  photoBtnBg: string
  photoBtnBorder: string
  photoBtnText: string
  rangeTrack: string
  rangeThumb: string
  textShadow: string
}

export function getPacerUiColors(
  hasPhoto: boolean,
  highContrast: boolean,
  palette: Palette,
): PacerUiColors {
  const text = palette.text
  const ring = palette.ring
  const bg = palette.bg

  if (hasPhoto) {
    const muted = highContrast ? 0.94 : 0.86
    const faint = highContrast ? 0.88 : 0.78
    return {
      shellBg: bg,
      paneBg: bg,
      paneBorder: 'none',
      overlay: highContrast ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.28)',
      chipBg: highContrast ? 'rgba(0,0,0,0.58)' : 'rgba(0,0,0,0.38)',
      phaseIdle: `rgba(255,255,255,${highContrast ? 0.98 : 0.92})`,
      phaseRunning: '#ffffff',
      circleIdleBg: 'rgba(0,0,0,0.18)',
      countColor: '#ffffff',
      countSubColor: `rgba(255,255,255,${faint})`,
      beginColor: highContrast ? '#b8f09a' : '#9fe07a',
      beginBorder: highContrast ? '#b8f09a' : '#9fe07a',
      beginRunningColor: `rgba(255,255,255,${faint})`,
      beginRunningBorder: `rgba(255,255,255,${highContrast ? 0.5 : 0.35})`,
      doneColor: `rgba(255,255,255,${muted})`,
      doneHover: 'rgba(255,255,255,0.98)',
      panelBg: highContrast ? 'rgba(0,0,0,0.58)' : 'rgba(0,0,0,0.38)',
      labelMuted: `rgba(255,255,255,${muted})`,
      sliderEnds: `rgba(255,255,255,${faint})`,
      timing: `rgba(255,255,255,${faint})`,
      dotInactive: 'rgba(255,255,255,0.28)',
      addPhoto: withAlpha(text, highContrast ? 0.75 : 0.55),
      addPhotoBorder: withAlpha(text, 0.35),
      addPhotoBorderActive: ring,
      brand: withAlpha(text, highContrast ? 0.55 : 0.4),
      photoBtnBg: highContrast ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,0.65)',
      photoBtnBorder: withAlpha(text, highContrast ? 0.35 : 0.22),
      photoBtnText: withAlpha(text, highContrast ? 0.9 : 0.72),
      rangeTrack: 'rgba(255,255,255,0.35)',
      rangeThumb: ring,
      textShadow: '0 1px 4px rgba(0,0,0,0.45)',
    }
  }

  const muted = highContrast ? 0.9 : 0.82
  const faint = highContrast ? 0.78 : 0.68
  const chip = highContrast ? 0.1 : 0.07

  return {
    shellBg: bg,
    paneBg: bg,
    paneBorder: `1.5px solid ${withAlpha(ring, highContrast ? 0.75 : 0.45)}`,
    overlay: 'transparent',
    chipBg: withAlpha(text, chip),
    phaseIdle: withAlpha(text, highContrast ? 0.95 : 0.88),
    phaseRunning: text,
    circleIdleBg: withAlpha(text, highContrast ? 0.12 : 0.08),
    countColor: text,
    countSubColor: withAlpha(text, faint),
    beginColor: highContrast ? '#1f6b35' : '#2d7a3a',
    beginBorder: highContrast ? '#1f6b35' : '#2d7a3a',
    beginRunningColor: withAlpha(text, faint),
    beginRunningBorder: withAlpha(ring, highContrast ? 0.65 : 0.45),
    doneColor: withAlpha(text, muted),
    doneHover: text,
    panelBg: withAlpha(text, chip),
    labelMuted: withAlpha(text, muted),
    sliderEnds: withAlpha(text, faint),
    timing: withAlpha(text, faint),
    dotInactive: withAlpha(text, highContrast ? 0.22 : 0.16),
    addPhoto: withAlpha(text, highContrast ? 0.72 : 0.58),
    addPhotoBorder: withAlpha(text, highContrast ? 0.45 : 0.3),
    addPhotoBorderActive: ring,
    brand: withAlpha(text, highContrast ? 0.5 : 0.38),
    photoBtnBg: withAlpha(bg, 0.92),
    photoBtnBorder: withAlpha(ring, highContrast ? 0.65 : 0.45),
    photoBtnText: withAlpha(text, highContrast ? 0.92 : 0.78),
    rangeTrack: withAlpha(text, highContrast ? 0.22 : 0.14),
    rangeThumb: ring,
    textShadow: 'none',
  }
}
